import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Star, Save, Send, CalendarOff, Trash2 } from "lucide-react";
import { toast } from "sonner";
import DocumentsPanel from "@/components/app/DocumentsPanel";
import EarningsTab from "@/components/app/EarningsTab";
import Avatar from "@/components/app/Avatar";
import { uploadFile } from "@/lib/upload";
import { useAuth } from "@/contexts/AuthContext";

const FIELDS = [
  ["birth_date", "Data di nascita", "date"], ["birth_place", "Luogo di nascita"], ["fiscal_code", "Codice fiscale"],
  ["address", "Indirizzo di residenza"], ["city", "Città"], ["iban", "IBAN per lo stipendio"],
  ["personal_email", "Email personale"],
  ["emergency_name", "Contatto di emergenza"], ["emergency_phone", "Telefono di emergenza"],
];
export const REQUEST_TYPES = {
  anticipo: "Anticipo stipendio", attrezzatura: "Attrezzatura / divisa",
  cambio_turno: "Cambio turno", documenti: "Documenti", altro: "Altro",
};
export const REQUEST_STATUS = { pending: "In attesa", approved: "Approvata", rejected: "Rifiutata", done: "Evasa" };

export function Stars({ score }) {
  return (
    <span className="inline-flex">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} className={`w-4 h-4 ${i <= score ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
      ))}
    </span>
  );
}

function PhotoCard() {
  const { user, refresh } = useAuth();
  const [pct, setPct] = useState(null);
  const upload = async (file) => {
    try {
      setPct(0);
      const upload_id = await uploadFile(file, setPct);
      await api.post("/me/photo", { upload_id });
      await refresh(); toast.success("Foto aggiornata");
    } catch (e) { toast.error(formatApiError(e)); } finally { setPct(null); }
  };
  return (
    <div className="flex items-center gap-4" data-testid="profile-photo-card">
      <Avatar user={user} size="w-16 h-16" />
      <label className="text-sm font-semibold text-sky-700 cursor-pointer">
        {pct !== null ? `Caricamento ${pct}%` : user?.photo_updated_at ? "Cambia foto" : "Aggiungi foto profilo"}
        <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" disabled={pct !== null}
          onChange={(e) => { const f = e.target.files[0]; e.target.value = ""; if (f) { upload(f); } }} data-testid="profile-photo-input" />
      </label>
    </div>
  );
}

function ProfileTab() {
  const [p, setP] = useState(null);
  useEffect(() => { api.get("/me/profile").then((r) => setP(r.data.profile || {})); }, []);
  const save = async () => {
    try { await api.put("/me/profile", p); toast.success("Dati salvati"); } catch (e) { toast.error(formatApiError(e)); }
  };
  if (!p) return null;
  return (
    <div className="space-y-4" data-testid="profile-form">
      <PhotoCard />
      <div className="grid sm:grid-cols-2 gap-4">
        {FIELDS.map(([k, label, type]) => (
          <div key={k}>
            <Label>{label}</Label>
            <Input type={type || "text"} value={p[k] || ""} onChange={(e) => setP({ ...p, [k]: e.target.value })} data-testid={`profile-${k}`} />
          </div>
        ))}
      </div>
      <div>
        <Label>Note</Label>
        <Textarea rows={3} value={p.notes || ""} onChange={(e) => setP({ ...p, notes: e.target.value })} data-testid="profile-notes" />
      </div>
      <Button onClick={save} className="bg-sky-600 hover:bg-sky-700 text-white" data-testid="profile-save-button">
        <Save className="w-4 h-4 mr-2" /> Salva dati
      </Button>
    </div>
  );
}

function ReviewsTab() {
  const [list, setList] = useState([]);
  useEffect(() => { api.get("/me/reviews").then((r) => setList(r.data)); }, []);
  const avg = list.length ? list.reduce((a, r) => a + r.score, 0) / list.length : 0;
  return (
    <div className="space-y-4" data-testid="reviews-list">
      {list.length === 0 ? (
        <p className="text-slate-400 text-sm">Nessuna valutazione ancora.</p>
      ) : (
        <>
          <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-xl p-4">
            <span className="font-display text-3xl font-extrabold text-amber-700" data-testid="reviews-average">{avg.toFixed(1)}</span>
            <div><Stars score={Math.round(avg)} /><div className="text-xs text-slate-500">Media su {list.length} valutazioni</div></div>
          </div>
          <ul className="space-y-3">
            {list.map((r) => (
              <li key={r.id} className="border border-slate-200 rounded-xl p-4">
                <div className="flex items-center justify-between"><Stars score={r.score} /><span className="text-xs text-slate-400">{r.created_at.slice(0, 10)}</span></div>
                {r.feedback && <p className="text-sm text-slate-700 mt-2 whitespace-pre-wrap">{r.feedback}</p>}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

function RequestsTab() {
  const [list, setList] = useState([]);
  const [form, setForm] = useState({ type: "altro", message: "" });
  const load = () => api.get("/employee-requests").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);
  const send = async (e) => {
    e.preventDefault();
    try { await api.post("/employee-requests", form); toast.success("Richiesta inviata"); setForm({ type: "altro", message: "" }); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const remove = async (id) => {
    try { await api.delete(`/employee-requests/${id}`); load(); } catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <div className="space-y-6">
      <form onSubmit={send} className="space-y-3 border border-slate-200 rounded-xl p-4" data-testid="request-form">
        <Label>Tipo di richiesta</Label>
        <select className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm bg-white" value={form.type}
          onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="request-type-select">
          {Object.entries(REQUEST_TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <Textarea rows={3} required placeholder="Scrivi la tua richiesta…" value={form.message}
          onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="request-message-input" />
        <Button type="submit" className="bg-sky-600 hover:bg-sky-700 text-white" data-testid="request-submit-button">
          <Send className="w-4 h-4 mr-2" /> Invia richiesta
        </Button>
      </form>
      <ul className="space-y-3" data-testid="my-requests-list">
        {list.map((r) => (
          <li key={r.id} className="border border-slate-200 rounded-xl p-4">
            <div className="flex items-center justify-between gap-2">
              <span className="font-semibold text-sm text-slate-900">{REQUEST_TYPES[r.type]}</span>
              <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">{REQUEST_STATUS[r.status]}</span>
            </div>
            <p className="text-sm text-slate-600 mt-1 whitespace-pre-wrap">{r.message}</p>
            {r.reply && <p className="text-xs text-sky-700 mt-2">Risposta: {r.reply}</p>}
            {r.status === "pending" && (
              <button onClick={() => remove(r.id)} className="mt-2 text-xs text-rose-600 inline-flex items-center gap-1" data-testid={`delete-request-${r.id}`}>
                <Trash2 className="w-3 h-3" /> Ritira
              </button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function PersonalArea() {
  return (
    <div className="space-y-6" data-testid="personal-area-page">
      <header>
        <div className="text-xs uppercase tracking-widest text-sky-600 font-semibold mb-2">Il mio profilo</div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">Area Personale</h1>
      </header>
      <Link to="/ferie" className="flex items-center gap-3 bg-sky-50 border border-sky-200 rounded-xl p-4 text-sky-800 font-semibold text-sm hover:bg-sky-100 transition-colors" data-testid="personal-leaves-link">
        <CalendarOff className="w-5 h-5" /> Chiedi ferie, riposi o permessi
      </Link>
      <Tabs defaultValue="profile" className="bg-white rounded-2xl border border-slate-200 p-4 sm:p-6">
        <TabsList className="mb-4 flex-wrap h-auto">
          <TabsTrigger value="profile" data-testid="tab-profile">Dati anagrafici</TabsTrigger>
          <TabsTrigger value="reviews" data-testid="tab-reviews">Valutazioni</TabsTrigger>
          <TabsTrigger value="requests" data-testid="tab-requests">Richieste</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents">Documenti</TabsTrigger>
          <TabsTrigger value="earnings" data-testid="tab-earnings">Compensi</TabsTrigger>
        </TabsList>
        <TabsContent value="profile"><ProfileTab /></TabsContent>
        <TabsContent value="reviews"><ReviewsTab /></TabsContent>
        <TabsContent value="requests"><RequestsTab /></TabsContent>
        <TabsContent value="documents"><DocumentsPanel /></TabsContent>
        <TabsContent value="earnings"><EarningsTab /></TabsContent>
      </Tabs>
    </div>
  );
}
